import React, { useEffect, useState } from 'react'
import SimpleModal from '../../components/SimpleModal'
import Spinner from '../../components/Spinner'
import LevelPill from '../../components/badges/LevelPill'
import { useGetTeamsQuery, useSearchAccountsQuery } from '../../api/client'

export type DeployTarget = { kind: 'team' | 'profile'; id: number }

type Props = {
    open: boolean
    busy: boolean
    onClose: () => void
    onAdd: (picks: DeployTarget[]) => void
}

type Scope = 'All' | 'Profile' | 'Team'
const SCOPES: Scope[] = ['All', 'Profile', 'Team']

export default function ApplyClassifierModal({ open, busy, onClose, onAdd }: Props) {
    const [query, setQuery] = useState('')
    const [scope, setScope] = useState<Scope>('All')
    const [selected, setSelected] = useState<DeployTarget[]>([])

    // Reset search/scope/selection whenever the modal closes (including after Add,
    // which the parent closes directly) so it always reopens clean.
    useEffect(() => {
        if (!open) {
            setQuery('')
            setScope('All')
            setSelected([])
        }
    }, [open])

    const teamsQuery = useGetTeamsQuery({ skip: !open })
    const accountsQuery = useSearchAccountsQuery({
        variables: {
            params: { keyword: query.trim() || undefined, limit: 25, offset: 0, socialMediaServices: [], teamIds: [] }
        },
        skip: !open || scope === 'Team'
    })

    const teamCandidates =
        scope === 'Profile'
            ? []
            : (teamsQuery.data?.getTeams ?? [])
                  .filter((t) => !query.trim() || t.name.toLowerCase().includes(query.trim().toLowerCase()))
                  .map((t) => ({
                      kind: 'team' as const,
                      id: t.id,
                      name: t.name,
                      platform: null,
                      teamName: null
                  }))

    const profileCandidates =
        scope === 'Team'
            ? []
            : (accountsQuery.data?.searchAccounts.accounts ?? []).map((a) => ({
                  kind: 'profile' as const,
                  id: a.id,
                  name: a.accountName ?? `Profile #${a.id}`,
                  platform: a.socialMediaServiceDisplayName ?? null,
                  teamName: a.teamName ?? null
              }))

    const candidates = [...teamCandidates, ...profileCandidates]
    const loading = teamsQuery.loading || accountsQuery.loading

    function toggle(pick: DeployTarget) {
        setSelected((prev) =>
            prev.some((p) => p.kind === pick.kind && p.id === pick.id)
                ? prev.filter((p) => !(p.kind === pick.kind && p.id === pick.id))
                : [...prev, pick]
        )
    }

    function close() {
        setQuery('')
        setScope('All')
        setSelected([])
        onClose()
    }

    return (
        <SimpleModal open={open} onClose={close}>
            <div className="w-[560px] max-w-[94vw] overflow-hidden rounded-xl bg-white text-left text-sm text-ink-800 shadow-2xl">
                <div className="border-b border-ink-100 px-5 py-4">
                    <h3 className="m-0 font-sans text-lg font-medium text-ink-900">Apply to profiles</h3>
                    <p className="mb-0 mt-1 text-[12.5px] text-ink-500">
                        Search by profile or team. Adding a team applies the classifier to each of its
                        current profiles — a one-time application, not a standing link.
                    </p>
                </div>

                <div className="border-b border-ink-100 px-5 pb-3 pt-4">
                    <input
                        type="search"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search profiles or teams…"
                        className="w-full rounded-lg border border-ink-200 px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:outline-none focus:ring-1 focus:ring-arwen-300"
                    />
                    <div className="mt-2.5 inline-flex overflow-hidden rounded-lg border border-ink-200" role="group" aria-label="Search scope">
                        {SCOPES.map((s) => (
                            <button
                                key={s}
                                type="button"
                                onClick={() => setScope(s)}
                                className={`border-l border-ink-200 px-3 py-1.5 text-xs font-semibold first:border-l-0 ${
                                    scope === s ? 'bg-arwen-600 text-white' : 'bg-white text-ink-700 hover:bg-ink-50'
                                }`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="max-h-[320px] overflow-y-auto px-2 py-2">
                    {loading && candidates.length === 0 ? (
                        <div className="py-8 text-center">
                            <Spinner size={4} />
                        </div>
                    ) : candidates.length === 0 ? (
                        <div className="px-3 py-8 text-center text-xs text-ink-500">No matches.</div>
                    ) : (
                        candidates.map((c) => {
                            const isChecked = selected.some((p) => p.kind === c.kind && p.id === c.id)
                            return (
                                <label
                                    key={`${c.kind}-${c.id}`}
                                    className={`flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 ${
                                        isChecked ? 'bg-arwen-50' : 'hover:bg-ink-50'
                                    }`}
                                >
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => toggle({ kind: c.kind, id: c.id })}
                                        className="h-4 w-4 flex-none accent-arwen-600"
                                    />
                                    <span className="flex items-center gap-2">
                                        <span className="font-semibold text-ink-900">{c.name}</span>
                                        <LevelPill level={c.kind} />
                                    </span>
                                    {c.kind === 'profile' && (
                                        <span className="ml-auto text-xs text-ink-500">
                                            {[c.platform, c.teamName ?? 'No team'].filter(Boolean).join(' · ')}
                                        </span>
                                    )}
                                </label>
                            )
                        })
                    )}
                </div>

                <div className="flex items-center justify-between border-t border-ink-100 bg-ink-50 px-5 py-3.5">
                    <span className="text-xs text-ink-500">{selected.length} selected</span>
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={close}
                            className="rounded-lg border border-ink-200 bg-white px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={() => onAdd(selected)}
                            disabled={selected.length === 0 || busy}
                            className="rounded-lg bg-arwen-600 px-4 py-2 text-sm font-bold text-white hover:bg-arwen-700 disabled:opacity-40"
                        >
                            {busy ? 'Applying…' : `Add${selected.length > 0 ? ` ${selected.length}` : ''}`}
                        </button>
                    </div>
                </div>
            </div>
        </SimpleModal>
    )
}
